import { applyClaimAdjustment } from "./claim-adjustment";
import { resolvePia } from "./pia";
import type { PersonInputs } from "./types";

/**
 * Survivor benefit (simplified).
 *
 * After the higher earner dies, the surviving spouse generally receives the higher of:
 *   - their own claim-adjusted benefit
 *   - the deceased's benefit amount at the time of death (including any DRCs the deceased earned)
 *
 * For planning purposes we treat the survivor benefit as the deceased higher earner's
 * claim-adjusted monthly benefit at the higher earner's chosen claim age. This is the amount
 * "locked in" for survivor purposes when the higher earner claims.
 *
 * The survivor-optimization hint reflects the well-known insight: if the higher earner delays
 * to 70, the surviving spouse benefits for the rest of their life.
 */
export function survivorBenefit(params: {
  higherEarner: PersonInputs;
}): number {
  const { higherEarner } = params;
  const birthYear = new Date(higherEarner.dob).getUTCFullYear();
  const pia = resolvePia(higherEarner);
  return applyClaimAdjustment({
    pia,
    birthYear,
    claimAge: higherEarner.claimAge,
  }).monthlyBenefit;
}

// Author-judgment thresholds (NOT SSA rules). The 69.5 and 67 boundaries
// reflect editorial intuition about when delaying the higher earner has an
// outsized survivor-side impact. See docs/sources.md §5 if these change.
export function survivorOptimizationHint(higherEarnerClaimAge: number): string {
  if (higherEarnerClaimAge >= 69.5) {
    return "The higher earner is already claiming near 70 — the survivor benefit is being maximized for whichever spouse lives longer.";
  }
  if (higherEarnerClaimAge >= 67) {
    return "Delaying the higher earner's claim closer to 70 would meaningfully grow the survivor benefit for whichever spouse lives longer.";
  }
  return "Claiming the higher earner's benefit early locks in a smaller survivor benefit. If longevity for either spouse is plausible, delaying the higher earner toward 70 has an outsized effect on lifetime household income.";
}
